import { useEffect, useState, type CSSProperties } from 'react';
import { C } from '@ridgewill/brand';
import { supportToken } from '../lib/fleet-token.js';
import { usePerson, usePersonName } from '../lib/people.js';
import { stamp } from '../lib/format.js';
import { useAction } from '../lib/useAsync.js';
import { deletedAttachments, supportApi, type DeletedAttachment, type TicketEvent, type TicketMessage } from '../lib/support-api.js';
import { Modal, ModalActions } from '../components/Modal.js';
import { Avatar, Badge, Button, Note, OutcomeNote, When } from '../components/ui.js';

/**
 * A ticket's conversation as a chat timeline (Jon, 2026-10-17, after
 * ServiceNow's ticket view).
 *
 * What we RECEIVED sits on the left and what we SENT on the right, each card
 * pointing at the author's avatar on a line down the middle. Newest at the
 * top, so the message somebody opened the ticket to answer is the first thing
 * they read, and the thread ends at "Opened". Internal notes sit on our side,
 * tinted so they cannot be mistaken for something the requester saw. When
 * the thread itself is narrower than 640px (not the window: the sidebar takes
 * a share of that) it folds into one column with the line on the left.
 */
const api = supportApi(supportToken);

type Side = 'received' | 'sent';

/** The request side is what came in, including a request staff typed for somebody. */
export function sideOf(m: Pick<TicketMessage, 'direction'>): Side {
  return m.direction === 'INBOUND' ? 'received' : 'sent';
}

/** Newest first, ties kept in the order support sent them. */
export function newestFirst<M extends Pick<TicketMessage, 'at'>>(messages: readonly M[]): M[] {
  return messages
    .map((m, i) => ({ m, i }))
    .sort((a, b) => Date.parse(b.m.at) - Date.parse(a.m.at) || b.i - a.i)
    .map(({ m }) => m);
}

export function kindLabel(m: Pick<TicketMessage, 'direction' | 'trust'>): string {
  switch (m.direction) {
    case 'INBOUND':
      /**
       * A description staff wrote when opening the ticket for somebody. It is
       * the requester's request, shown in their thread, but they did not type
       * it, and the thread must not say they did.
       */
      return m.trust === 'STAFF' ? 'Request, written by staff' : 'From the requester';
    case 'OUTBOUND':
      return 'Reply';
    case 'INTERNAL_NOTE':
      return 'Internal note';
    default:
      return 'System';
  }
}

const FOLD_BELOW = 640;

/** Whether the element is narrower than `FOLD_BELOW`, kept current as it resizes. */
function useNarrow(): [boolean, (el: HTMLDivElement | null) => void] {
  const [el, setEl] = useState<HTMLDivElement | null>(null);
  const [narrow, setNarrow] = useState(false);
  useEffect(() => {
    if (!el) return;
    const measure = () => setNarrow(el.clientWidth < FOLD_BELOW);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [el]);
  return [narrow, setEl];
}

/** What the thread needs to offer and show attachment deletes (auth#672). */
export interface AttachmentDeletes {
  /** The ticket's events, where a deleted attachment is recorded. */
  events: readonly TicketEvent[];
  /** Support's answer for the signed-in person. */
  allowed: boolean;
  /** Re-read the ticket after a delete. */
  onDeleted: () => void;
}

const NO_DELETES: AttachmentDeletes = { events: [], allowed: false, onDeleted: () => {} };

export function Conversation({
  reference,
  messages,
  openedAt,
  me,
  deletes = NO_DELETES,
}: {
  reference: string;
  messages: readonly TicketMessage[];
  openedAt: string;
  me?: string;
  deletes?: AttachmentDeletes;
}) {
  const [narrow, measured] = useNarrow();
  const deleted = deletedAttachments(deletes.events);
  if (messages.length === 0) return <p style={{ color: C.muted, margin: 0 }}>No messages.</p>;

  return (
    <div ref={measured} style={{ position: 'relative' }}>
      <div aria-hidden style={{ ...t.line, left: narrow ? AVATAR / 2 - 1 : 'calc(50% - 1px)' }} />
      <ol style={t.list} aria-label="Conversation, newest first">
        {newestFirst(messages).map((m) => (
          <Entry
            key={m.id}
            reference={reference}
            message={m}
            me={me}
            narrow={narrow}
            deleted={deleted.get(m.id) ?? []}
            deletes={deletes}
          />
        ))}
      </ol>
      <div style={{ ...t.start, ...(narrow ? { alignItems: 'flex-start' } : {}) }}>
        <span style={t.startDot}>Opened</span>
        <span style={{ color: C.muted, fontSize: '.8rem' }}>
          <When iso={openedAt} />
        </span>
      </div>
    </div>
  );
}

const AVATAR = 40;

function Entry({
  reference,
  message: m,
  me,
  narrow,
  deleted,
  deletes,
}: {
  reference: string;
  message: TicketMessage;
  me?: string;
  narrow: boolean;
  deleted: DeletedAttachment[];
  deletes: AttachmentDeletes;
}) {
  const side = sideOf(m);
  const person = usePerson(m.authorUserId && m.authorUserId !== me ? m.authorUserId : null);
  const meToo = usePerson(m.authorUserId && m.authorUserId === me ? m.authorUserId : null);
  const name =
    m.direction === 'SYSTEM'
      ? 'Ridgewill Support'
      : m.authorUserId && m.authorUserId === me
        ? 'You'
        : (person?.name ?? m.authorEmail ?? (m.authorUserId ? m.authorUserId.slice(0, 8) : 'Unknown sender'));
  const image = (m.authorUserId === me ? meToo : person)?.image ?? null;
  const avatarId = m.authorUserId ?? m.authorEmail ?? m.id;
  const avatarLabel = m.authorUserId === me ? (meToo?.name ?? 'You') : name;

  const card = (
    <Card reference={reference} message={m} name={name} side={narrow ? 'sent' : side} deleted={deleted} deletes={deletes} me={me} />
  );
  const avatar = (
    <div style={t.avatarCell}>
      <span style={t.avatarRing}>
        <Avatar id={avatarId} label={avatarLabel} image={image} size={AVATAR} />
      </span>
    </div>
  );

  if (narrow) {
    return (
      <li style={{ ...t.row, gridTemplateColumns: `${AVATAR}px 1fr` }}>
        {avatar}
        {card}
      </li>
    );
  }
  return (
    <li style={t.row}>
      {side === 'received' ? card : <span />}
      {avatar}
      {side === 'sent' ? card : <span />}
    </li>
  );
}

function Card({
  reference,
  message: m,
  name,
  side,
  deleted,
  deletes,
  me,
}: {
  reference: string;
  message: TicketMessage;
  name: string;
  /** Which side of the line the card is on; its pointer faces the line. */
  side: Side;
  deleted: DeletedAttachment[];
  deletes: AttachmentDeletes;
  me?: string;
}) {
  const [opening, setOpening] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<{ id: string; filename: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const note = m.direction === 'INTERNAL_NOTE';
  const background = note ? C.violetTint : C.page;
  const border = note ? C.violetTint : C.border;

  return (
    <div style={{ ...t.card, background, borderColor: border }}>
      <span
        aria-hidden
        style={{
          ...t.pointer,
          background,
          borderColor: border,
          ...(side === 'received'
            ? { right: -7, borderLeft: 'none', borderBottom: 'none' }
            : { left: -7, borderRight: 'none', borderTop: 'none' }),
        }}
      />
      <div style={t.name}>{name}</div>
      <div style={t.meta}>
        <When iso={m.at} />
        <span aria-hidden>·</span>
        <span style={note ? { color: C.violetText, fontWeight: 600 } : undefined}>
          {kindLabel(m)}
          {note ? ' (staff only)' : ''}
        </span>
        {m.authorIsAgent ? <Badge>agent</Badge> : null}
        {m.trust === 'UNVERIFIED' ? <Badge tone="warn">unverified sender</Badge> : null}
        {m.trust !== 'STAFF' && m.authorUserId && m.authorEmail ? <span>{m.authorEmail}</span> : null}
      </div>
      <div style={t.body}>{m.body}</div>
      {confirming ? (
        <DeleteAttachment
          reference={reference}
          attachment={confirming}
          onClose={() => setConfirming(null)}
          onDeleted={() => {
            setConfirming(null);
            deletes.onDeleted();
          }}
        />
      ) : null}
      {m.attachments.length || deleted.length ? (
        <div style={t.attachments}>
          {m.attachments.map((a) => (
            <span key={a.id} style={t.attachment}>
              <Button
                disabled={opening === a.id || a.scan === 'REJECTED'}
                title={a.scan === 'REJECTED' ? 'Refused by the scanner' : `${a.bytes.toLocaleString()} bytes`}
                onClick={() => {
                  setOpening(a.id);
                  setError(null);
                  api
                    .attachment(reference, a.id)
                    .then((signed) => window.open(signed.url, '_blank', 'noopener,noreferrer'))
                    .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)))
                    .finally(() => setOpening(null));
                }}
              >
                {a.isDiagnosticLog ? 'Log: ' : ''}
                {a.filename}
              </Button>
              {deletes.allowed ? (
                <Button tone="danger" title={`Delete ${a.filename}`} onClick={() => setConfirming({ id: a.id, filename: a.filename })}>
                  Delete
                </Button>
              ) : null}
            </span>
          ))}
          {deleted.map((d) => (
            <DeletedFile key={d.attachmentId} file={d} me={me} />
          ))}
        </div>
      ) : null}
      {error ? <Note tone="danger">{error}</Note> : null}
    </div>
  );
}

/**
 * "Deleted by NAME on DATE", where the file was (auth#672). The filename stays
 * so the thread still reads ("the photo of my license"), the file does not.
 */
function DeletedFile({ file, me }: { file: DeletedAttachment; me?: string }) {
  const name = usePersonName(file.actorUserId && file.actorUserId !== me ? file.actorUserId : null);
  const who =
    file.actorUserId && file.actorUserId === me
      ? 'you'
      : (name ?? (file.actorUserId ? file.actorUserId.slice(0, 8) : 'staff'));
  return (
    <span style={t.deleted} title={file.filename}>
      <span style={{ textDecoration: 'line-through' }}>{file.filename}</span>
      <span>
        Deleted by {who} on {stamp(file.at)}
      </span>
    </span>
  );
}

/** The confirm step. The wording is Jon's (auth#672): it is gone for good. */
function DeleteAttachment({
  reference,
  attachment,
  onClose,
  onDeleted,
}: {
  reference: string;
  attachment: { id: string; filename: string };
  onClose: () => void;
  onDeleted: () => void;
}) {
  const action = useAction();
  return (
    <Modal
      title="Delete attachment"
      subtitle={attachment.filename}
      onClose={onClose}
      width={460}
      footer={
        <ModalActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            tone="danger"
            disabled={action.running}
            onClick={() =>
              void action
                .run(async () => {
                  await api.deleteAttachment(reference, attachment.id);
                  return 'Deleted.';
                })
                .then((ok) => {
                  if (ok) onDeleted();
                })
            }
          >
            {action.running ? 'Deleting…' : 'Delete'}
          </Button>
        </ModalActions>
      }
    >
      <p style={{ margin: 0, fontSize: '.9rem', lineHeight: 1.5 }}>
        Delete this attachment? It is removed for good. Use this after you have verified an ID.
      </p>
      <OutcomeNote outcome={action.outcome} />
    </Modal>
  );
}

const t: Record<string, CSSProperties> = {
  // Stops at the Opened marker, not under the date beneath it.
  line: { position: 'absolute', top: 0, bottom: '1.5rem', width: 2, background: C.border },
  list: { listStyle: 'none', margin: 0, padding: 0, position: 'relative', display: 'flex', flexDirection: 'column', gap: '1rem' },
  row: { display: 'grid', gridTemplateColumns: `1fr ${AVATAR + 24}px 1fr`, alignItems: 'start', gap: 0 },
  avatarCell: { display: 'flex', justifyContent: 'center', paddingTop: '.55rem' },
  avatarRing: { borderRadius: '50%', boxShadow: `0 0 0 4px ${C.surface}`, display: 'inline-flex' },
  card: {
    position: 'relative',
    border: '1px solid',
    borderRadius: 8,
    padding: '.75rem .95rem .85rem',
    minWidth: 0,
    boxShadow: '0 1px 2px rgba(0,0,0,.06)',
  },
  pointer: { position: 'absolute', top: 20, width: 12, height: 12, border: '1px solid', transform: 'rotate(45deg)' },
  name: { fontSize: '.98rem', fontWeight: 650, color: C.text, overflowWrap: 'anywhere' },
  meta: { display: 'flex', gap: '.4rem', alignItems: 'center', flexWrap: 'wrap', fontSize: '.78rem', color: C.muted, marginTop: '.1rem' },
  body: { whiteSpace: 'pre-wrap', fontSize: '.9rem', lineHeight: 1.55, marginTop: '.6rem', overflowWrap: 'anywhere' },
  attachments: { display: 'flex', gap: '.4rem', flexWrap: 'wrap', marginTop: '.6rem' },
  attachment: { display: 'inline-flex', gap: '.25rem', alignItems: 'center' },
  deleted: {
    display: 'inline-flex',
    gap: '.4rem',
    alignItems: 'center',
    flexWrap: 'wrap',
    fontSize: '.8rem',
    color: C.muted,
    border: `1px dashed ${C.border}`,
    borderRadius: 6,
    padding: '.3rem .55rem',
  },
  start: { position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '.25rem', marginTop: '1rem' },
  startDot: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 64,
    height: 30,
    padding: '0 .8rem',
    borderRadius: 15,
    background: C.successBg,
    color: C.successText,
    fontSize: '.8rem',
    fontWeight: 650,
    boxShadow: `0 0 0 4px ${C.surface}`,
  },
};
